/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import {
  registerParam,
  registerForm,
  unegisterParam,
  unregisterForm,
  validateParam,
  validateForm,
  PaymentTypes,
  htmlTranslation,
  constants
} from '@pmi/pmi-iqos-shared-react'
import {
  Button,
  BlockingLoader,
  Checkbox,
  Container,
  FieldText,
  Icon,
  ListContainer,
  TextBody,
  Link
} from '@pmi/pmi-iqos-ds-react'
import {
  AdyenCheckoutStyleOverride,
  CreditCardExpiryDate
} from '@dcx-common/components'

const adyenDropinId = 'dropin-container'
const CardExperyStatus = {
  OK: 'Ok',
  NEARLY_EXPIRED: 'NearlyExpired',
  EXPIRED: 'Expired'
}
// Constant to help multiple cards implementation
const SHOW_MULTIPLE_CARDS = false

const CheckoutCreditCard = ({
  allowStoringCard,
  method,
  paymentMethods,
  translations,
  selectedPaymentMethodIndex,
  hasAdyenPaymentMethod,
  clearAdyenCheckoutForm,
  setMainStateProperty,
  isOpen,
  isReady,
  toggleStorePaymentMethod,
  handleDeleteCreditCard,
  handleEditCreditCard,
  line
}) => {
  const [dropin, setDropin] = useState(null)
  const [isNewCreditCardFormShown, setIsNewCreditCardFormShown] =
    useState(false)
  const [storedCardsState, setStoredCardsState] = useState([])
  const [selectedStoredCard, setSelectedStoredCard] = useState(null) // {index: 0, card: {}}
  const [adyenStoredPaymentMethodId, setAdyenStoredPaymentMethodId] =
    useState('')
  const [showEditCreditCardForm, setShowEditCreditCardForm] = useState(false)
  const [editFormFields, setEditFormFields] = useState({
    expiryDate: '',
    holderName: ''
  })
  const [editFormFieldsErros, setEditFormFieldsErros] = useState({
    expiryDate: [],
    holderName: []
  })
  const [isEditFormValid, setIsEditFormValid] = useState(true)
  const componentIsMounted = useRef(true)
  const [deletedCreditCardId, setDeletedCreditCardId] = useState('')
  const [forceExpiryDateErrorUIFix, setForceExpiryDateErrorUIFix] = useState('')
  // To control loading of multiple dropins
  const [init, setInit] = useState('')
  const [last, setLast] = useState('')

  const adyenExpiryDate = paymentMethods.find(
    p => p.paymentType === PaymentTypes.CREDIT_CARD
  ).minimumExpiryDate

  const formFieldNames = {
    expiryDate: 'expiryDate',
    holderName: 'holderName'
  }
  const formId = registerForm()
  const validators = {
    [formFieldNames.expiryDate]: registerParam(
      [
        {
          type: 'expiryDate',
          ...(adyenExpiryDate && {
            rule: adyenExpiryDate
          }),
          description: translations.tk_expiryDateValidationError
        }
      ],
      false,
      formId,
      editFormFields.expiryDate,
      errors => {
        setEditFormFieldsErros({ ...editFormFieldsErros, expiryDate: errors })
      }
    ),
    [formFieldNames.holderName]: registerParam(
      [],
      false,
      formId,
      editFormFields.holderName,
      errors => {
        setEditFormFieldsErros({ ...editFormFieldsErros, holderName: errors })
      }
    )
  }

  useEffect(() => {
    return () => {
      componentIsMounted.current = false
      unregisterForm(formId)
      Object.keys(formFieldNames).forEach(fieldNameKey =>
        unegisterParam(validators[formFieldNames[fieldNameKey]])
      )
    }
  }, [])

  useEffect(() => {
    // If the method isn't opened, we force the loading state
    if (
      paymentMethods[selectedPaymentMethodIndex].paymentType !==
      method.paymentType
    ) {
      setInit('')
    }
  }, [paymentMethods, method])

  useEffect(() => {
    if (hasAdyenPaymentMethod && componentIsMounted.current) {
      setupAdyenCheckout()
    }
  }, [componentIsMounted.current])

  useEffect(() => {
    if (hasAdyenPaymentMethod && clearAdyenCheckoutForm) {
      if (!window.AdyenWeb) {
        setupAdyenCheckout()
      } else {
        dropin && dropin.unmount()
        initAdyenCheckout()
      }
      setMainStateProperty('clearAdyenCheckoutForm', false)
    }
  }, [
    selectedPaymentMethodIndex,
    isOpen,
    hasAdyenPaymentMethod,
    clearAdyenCheckoutForm
  ])

  useEffect(() => {
    let storedCard = null
    if (
      adyenStoredPaymentMethodId &&
      storedCardsState &&
      storedCardsState.length
    ) {
      storedCardsState.forEach((card, index) => {
        if (card.id === adyenStoredPaymentMethodId) {
          storedCard = { index, card: { ...card } }
        }
      })
    }
    setSelectedStoredCard(storedCard)
    adyenCardsOverrridesContent(storedCard)
  }, [adyenStoredPaymentMethodId, storedCardsState])

  useEffect(() => {
    if (dropin && selectedStoredCard) {
      setMainStateProperty(
        'adyenCheckoutIsValid',
        validateAdyenCheckout(dropin && dropin.isValid, selectedStoredCard)
      )
    }
  }, [selectedStoredCard])

  useEffect(() => {
    if (showEditCreditCardForm) {
      setEditFormFields({
        expiryDate: `${selectedStoredCard.card.expiryMonth}${constants.EXPIRY_DATE_DELIMITER}${selectedStoredCard.card.expiryYear}`,
        holderName: selectedStoredCard.card.holderName
      })
    } else {
      setEditFormFields({
        expiryDate: '',
        holderName: ''
      })
    }
  }, [showEditCreditCardForm])

  useEffect(() => {
    setEditFormFieldsErros({
      expiryDate: validateParam(
        editFormFields.expiryDate,
        validators[formFieldNames.expiryDate],
        formId
      ),
      holderName: validateParam(
        editFormFields.holderName,
        validators[formFieldNames.holderName],
        formId
      )
    })
    setIsEditFormValid(validateForm(formId, true))
  }, [editFormFields])

  const adyenCardsOverrrides = storedCards => {
    if (storedCards.length) {
      setTimeout(() => {
        // Stored card name and expire date override
        const cardsNamesElms =
          document.querySelectorAll('.adyen-checkout__payment-method__name') ||
          []
        cardsNamesElms.length &&
          storedCards.forEach((card, index) => {
            if (cardsNamesElms[index]) {
              const overrrideElem =
                cardsNamesElms[index].parentNode &&
                cardsNamesElms[index].parentNode.querySelector('.overrride')
              let newOverrrideElem
              if (!overrrideElem) {
                newOverrrideElem = document.createElement('span')
                newOverrrideElem.classList.add('overrride')
                cardsNamesElms[index].parentNode.appendChild(newOverrrideElem)
              }
              renderCreditCardNameSection(
                card,
                overrrideElem || newOverrrideElem
              )
            }
          })
      }, 1)
    }
  }

  const adyenCardsOverrridesContent = storedCard => {
    if (storedCard) {
      setTimeout(() => {
        const storedCardsElems =
          document.querySelectorAll('.adyen-checkout__payment-method') || []
        if (storedCardsElems[storedCard.index]) {
          const cardContentElm = storedCardsElems[
            storedCard.index
          ].querySelector('.adyen-checkout__payment-method__details__content')
          if (cardContentElm) {
            // Expire date status message information
            const expCardElm =
              cardContentElm.parentNode.querySelector('.expiry-card')
            if (
              storedCard.card.expiryStatus &&
              storedCard.card.expiryStatus !== CardExperyStatus.OK
            ) {
              const confirmElm = cardContentElm.parentNode.querySelector(
                '.adyen-checkout__payment-method__disable-confirmation'
              )
              let newExpCardElm
              if (!expCardElm) {
                newExpCardElm = document.createElement('div')
                newExpCardElm.classList.add('expiry-card')
                cardContentElm.parentNode.insertBefore(
                  newExpCardElm,
                  confirmElm
                )
              }
              renderCreditCardExpiryStatusSection(
                storedCard.card.expiryStatus,
                expCardElm || newExpCardElm
              )
            } else if (expCardElm) {
              expCardElm.remove()
            }

            // Edit/delete buttons
            const actionsEl = cardContentElm.querySelector('.actions')
            let newActionsEl
            if (!actionsEl) {
              newActionsEl = document.createElement('div')
              newActionsEl.classList.add('actions')
              cardContentElm.appendChild(newActionsEl)
            }
            renderCreditCardActions(actionsEl || newActionsEl)
          }
        }
      }, 1)
    }
  }

  const renderCreditCardNameSection = (card, elem) => {
    createRoot(elem).render(
      <Container pl="4">
        <Container display={Container.displays.flex}>
          <TextBody
            value={`${card.holderName} - <strong>${card.lastFour}</strong>`}
            acceptsHtml
          />
        </Container>
        <Container pt="2">
          <TextBody
            acceptsHtml
            value={`<strong>${card.expiryMonth}/20${card.expiryYear}</strong>`}
          />
        </Container>
      </Container>
    )
  }

  const renderCreditCardExpiryStatusSection = (status, elem) => {
    createRoot(elem).render(
      <Container pb="4">
        <div className="adyen-checkout__payment-method__expired-msg">
          {translations['tk_creditCard' + status]}
        </div>
      </Container>
    )
  }

  const renderCreditCardActions = elem => {
    createRoot(elem).render(
      <Container
        display={Container.displays.flex}
        justifyContent={Container.justifyContentValues.between}
        px="4">
        <Link
          onClickHandler={() => {
            setShowEditCreditCardForm(true)
          }}
          data-testing="Checkout_Link_EditCreditCard">
          {translations.tk_editCreditCard}
        </Link>
        <Link
          onClickHandler={deleteConfirmElmClick}
          data-testing="Checkout_Link_DeleteCreditCard">
          {translations.tk_deleteCreditCard}
        </Link>
      </Container>
    )
  }

  const deleteConfirmElmClick = () => {
    const deleteElm = document.querySelector(
      '.adyen-checkout__button.adyen-checkout__button--link'
    )
    deleteElm && deleteElm.click()
  }

  const setupAdyenCheckout = deletedCreditCardId => {
    const script = document.createElement('script')
    script.src = constants.ADYEN_SCRIPT_URL
    script.onload = () => {
      initAdyenCheckout(deletedCreditCardId)
    }
    document.body.appendChild(script)

    const style = document.createElement('link')
    style.href = constants.ADYEN_STYLES_URL
    style.rel = 'stylesheet'
    document.body.appendChild(style)
  }

  const initAdyenCheckout = async (delCardId = deletedCreditCardId) => {
    const id = Date.now()
    setInit(id)

    const adyenPaymentMethod = paymentMethods.find(
      p => p.paymentType === PaymentTypes.CREDIT_CARD
    )
    const paymentOptions =
      adyenPaymentMethod && adyenPaymentMethod.paymentOptions
    let storedCards =
      adyenPaymentMethod &&
      adyenPaymentMethod.cards &&
      adyenPaymentMethod.cards.length
        ? SHOW_MULTIPLE_CARDS
          ? adyenPaymentMethod.cards
          : [adyenPaymentMethod.cards[0]]
        : []
    storedCards = delCardId
      ? storedCards.filter(c => c.id !== delCardId) // Needs to be reviewed when multi and delete two cards consequently
      : storedCards
    const showNewCreditCardForm =
      !allowStoringCard || !storedCards.length || SHOW_MULTIPLE_CARDS
    setIsNewCreditCardFormShown(showNewCreditCardForm)
    const showStoredCards =
      allowStoringCard && (!showNewCreditCardForm || SHOW_MULTIPLE_CARDS)
    setStoredCardsState(showStoredCards ? storedCards : [])
    setAdyenStoredPaymentMethodId('')

    const configuration = {
      showPayButton: false,
      clientKey: line
        ? constants.ADYEN_ORIGIN_KEY_LINE
        : constants.ADYEN_ORIGIN_KEY,
      locale: 'ja-JP',
      countryCode: 'JP',
      translations: {
        'ja-JP': {
          'storedPaymentMethod.disable.confirmation':
            translations.tk_deleteCreditCardConfirmation,
          'storedPaymentMethod.disable.confirmButton':
            translations.tk_deleteCreditCardConfirm,
          'storedPaymentMethod.disable.cancelButton':
            translations.tk_deleteCreditCardCancel
        }
      },
      environment: constants.ADN_ENVIRONMENT,
      onError: handleOnAdyenCheckoutError,
      onAdditionalDetails: null,
      paymentMethodsResponse: {
        paymentMethods: showNewCreditCardForm ? paymentOptions : undefined,
        storedPaymentMethods: showStoredCards ? storedCards : undefined
      },
      onChange: handleOnAdyenCheckoutChange
    }

    const dropinConfiguration = {
      paymentMethodsConfiguration: {
        showStoredPaymentMethods: allowStoringCard,
        card: {
          hasHolderName: true,
          holderNameRequired: true,
          hideCVC: !showNewCreditCardForm, // Review compatibility with storedCards when having multiple cards
          ...(adyenPaymentMethod.minimumExpiryDate && {
            minimumExpiryDate: adyenPaymentMethod.minimumExpiryDate
          }),
          placeholders: {
            cardNumber: translations.tk_cardNumber,
            expiryDate: translations.tk_cardExpiryDate,
            securityCodeThreeDigits:
              translations.tk_cardSecurityCodeThreeDigits,
            securityCodeFourDigits: translations.tk_cardSecurityCodeFourDigits,
            holderName: translations.tk_cardHolderName
          }
        },
        storedCard: {
          hideCVC: true
        }
      },
      showRemovePaymentMethodButton: allowStoringCard,
      onDisableStoredPaymentMethod: allowStoringCard
        ? handleAdyenDeleteCreditCard
        : undefined,
      onReady: () => {
        showStoredCards && adyenCardsOverrrides(storedCards)
        setTimeout(() => {
          setLast(id)
        }, 1000)
      }
    }

    const { AdyenCheckout, Dropin } = window.AdyenWeb
    const checkout = await AdyenCheckout(configuration)
    const d = new Dropin(checkout, dropinConfiguration).mount(
      '#' + adyenDropinId
    )
    setDropin(d)
  }

  const handleOnAdyenCheckoutError = () => {
    setTimeout(() => {
      const isExpiryDateError = document.querySelector(
        '.adyen-checkout__field--expiryDate.adyen-checkout__field--error'
      )
      setForceExpiryDateErrorUIFix(
        isExpiryDateError ? isExpiryDateError.offsetHeight : ''
      )
    }, 100)
  }

  const handleOnAdyenCheckoutChange = dropinState => {
    setMainStateProperty(
      'adyenCheckoutIsValid',
      validateAdyenCheckout(dropinState.isValid)
    )
    setMainStateProperty('adyenCheckoutData', dropinState.data)
    const newAdyenStoredPaymentMethodId =
      (dropinState.data &&
        dropinState.data.paymentMethod &&
        dropinState.data.paymentMethod.storedPaymentMethodId) ||
      ''
    setAdyenStoredPaymentMethodId(newAdyenStoredPaymentMethodId)
  }

  const validateAdyenCheckout = (
    dropinIsValid,
    cardData = selectedStoredCard
  ) => {
    if (
      cardData &&
      cardData.card.expiryStatus &&
      cardData.card.expiryStatus === CardExperyStatus.EXPIRED
    ) {
      return false
    }
    return dropinIsValid
  }

  const handleAdyenDeleteCreditCard = async (
    storedPaymentMethodId,
    resolve,
    reject
  ) => {
    if (storedPaymentMethodId) {
      try {
        await handleDeleteCreditCard(storedPaymentMethodId)
        setupAdyenCheckout(storedPaymentMethodId)
        setDeletedCreditCardId(storedPaymentMethodId)
        resolve()
      } catch (error) {
        reject()
        // Keep the confimation open
        deleteConfirmElmClick()
      }
    }
  }

  const handleExpiryDateChange = newExpiryDate => {
    setEditFormFields({ ...editFormFields, expiryDate: newExpiryDate })
  }

  const handleHolderNameChange = e => {
    const newHolderName = e && e.target && e.target.value
    setEditFormFields({ ...editFormFields, holderName: newHolderName })
  }

  const handleSaveEditCreditCard = async () => {
    if (validateForm(formId)) {
      const arr = editFormFields.expiryDate.split(
        constants.EXPIRY_DATE_DELIMITER
      )
      const newCreditCardData = {
        holderName: editFormFields.holderName,
        expiryMonth: arr[0],
        expiryYear: arr[1]
      }
      try {
        await handleEditCreditCard(selectedStoredCard.card.id, {
          ...newCreditCardData,
          expiryYear: '20' + newCreditCardData.expiryYear
        })
        const newCard = {
          ...selectedStoredCard.card,
          ...newCreditCardData,
          expiryStatus: CardExperyStatus.OK
        }
        const newSelectedStoredCard = {
          ...selectedStoredCard,
          card: { ...newCard }
        }
        setSelectedStoredCard(newSelectedStoredCard)
        const newStoredCardsState = [...storedCardsState]
        newStoredCardsState[newSelectedStoredCard.index] = { ...newCard }
        setStoredCardsState([...newStoredCardsState])

        setMainStateProperty(
          'adyenCheckoutIsValid',
          validateAdyenCheckout(dropin && dropin.isValid, newSelectedStoredCard)
        )

        adyenCardsOverrrides(newStoredCardsState)
        adyenCardsOverrridesContent(newSelectedStoredCard)

        setShowEditCreditCardForm(false)
      } catch (error) {}
    }
  }

  const loadingDropin = init !== last

  return (
    <React.Fragment key={method.paymentId || 0}>
      <Container
        display={
          paymentMethods[selectedPaymentMethodIndex].paymentType ===
          method.paymentType
            ? Container.displays.block
            : Container.displays.none
        }>
        {!method.paymentId ? (
          <Container pl="8">
            <TextBody
              size={TextBody.sizes.m}
              {...htmlTranslation(
                translations,
                'tk_creditCardPaymentMethodNotAvailable'
              )}
            />
          </Container>
        ) : (
          <Container>
            <Container pl="8" pb="6">
              <TextBody
                size={TextBody.sizes.m}
                {...htmlTranslation(
                  translations,
                  'tk_stepThreeCreditCardTopMessage'
                )}
              />
            </Container>
            <Container
              display={
                isReady ? Container.displays.block : Container.displays.none
              }
              borderColor={Container.borderColors.slate30}>
              <Container
                display={
                  loadingDropin
                    ? Container.displays.block
                    : Container.displays.none
                }
                position={Container.positions.relative}
                height="20">
                <BlockingLoader loading={loadingDropin} zIndex="2" />
              </Container>
              <Container
                display={
                  loadingDropin
                    ? Container.displays.none
                    : Container.displays.block
                }>
                <AdyenCheckoutStyleOverride
                  hasStoredCards={storedCardsState.length}
                  forceExpiryDateErrorUIFix={forceExpiryDateErrorUIFix}
                />
                {/** Adyen form */}
                <Container
                  display={
                    selectedStoredCard && showEditCreditCardForm
                      ? Container.displays.none
                      : Container.displays.block
                  }>
                  <div id={adyenDropinId} />
                </Container>
                {/** Edit credit card form */}
                {selectedStoredCard &&
                  selectedStoredCard.card &&
                  showEditCreditCardForm && (
                    <Container p="8">
                      <Container pb={['0', '4', '4']}>
                        <ListContainer
                          columns={[1, 1, 2]}
                          xBetween="4"
                          yBetween="4"
                          overflow={ListContainer.overflows.unset}>
                          <Container fullWidth>
                            <Container pb="2">
                              <TextBody
                                size={TextBody.sizes.s}
                                {...htmlTranslation(
                                  translations,
                                  'tk_editCreditCardLastFourLabel'
                                )}
                              />
                            </Container>
                            <FieldText
                              value={'**** ' + selectedStoredCard.card.lastFour}
                              name={'lastFour'}
                              label={''}
                              onChangeHandler={() => {}}
                              disabled
                              icon={Icon.images.cards}
                              data-testing="Checkout_FieldText_LastFour"
                            />
                          </Container>
                          <Container />
                        </ListContainer>
                      </Container>
                      <ListContainer
                        columns={[1, 1, 2]}
                        xBetween="4"
                        yBetween="4"
                        overflow={ListContainer.overflows.unset}>
                        <Container fullWidth>
                          <Container pb="2">
                            <TextBody
                              size={TextBody.sizes.s}
                              {...htmlTranslation(
                                translations,
                                'tk_editCreditCardExpireDateLabel'
                              )}
                            />
                          </Container>
                          <CreditCardExpiryDate
                            expiryDate={editFormFields.expiryDate}
                            fieldName={formFieldNames.expiryDate}
                            onExpiryDateChange={handleExpiryDateChange}
                            errors={editFormFieldsErros.expiryDate}
                          />
                        </Container>
                        <Container fullWidth>
                          <Container pb="2">
                            <TextBody
                              size={TextBody.sizes.s}
                              {...htmlTranslation(
                                translations,
                                'tk_editCreditCardHolderNameLabel'
                              )}
                            />
                          </Container>
                          <FieldText
                            value={editFormFields.holderName}
                            name={formFieldNames.holderName}
                            label={''}
                            onChangeHandler={handleHolderNameChange}
                            errors={editFormFieldsErros.holderName}
                            data-testing="Checkout_FieldText_HolderName"
                          />
                        </Container>
                      </ListContainer>
                      <Container
                        display={Container.displays.flex}
                        flexDirection={[
                          Container.flexDirections.column,
                          Container.flexDirections.column,
                          Container.flexDirections.row
                        ]}
                        justifyContent={Container.justifyContentValues.center}
                        alignItems={Container.alignItemsValues.center}
                        pt={['5', '5', '10']}>
                        <Container
                          fullWidth
                          pt={['5', '5', '0']}
                          px={['0', '0', '5']}>
                          <Button
                            text={translations.tk_editCreditCardCancel}
                            onClickHandler={() => {
                              setShowEditCreditCardForm(false)
                            }}
                            fullWidth
                            variant={Button.variants.secondary}
                            data-testing="Checkout_Button_CancelEditCreditCard"
                          />
                        </Container>
                        <Container
                          fullWidth
                          pt={['5', '5', '0']}
                          px={['0', '0', '5']}>
                          <Button
                            text={translations.tk_editCreditCardSave}
                            onClickHandler={handleSaveEditCreditCard}
                            disabled={!isEditFormValid}
                            fullWidth
                            variant={Button.variants.primary}
                            data-testing="Checkout_Button_SaveEditCreditCard"
                          />
                        </Container>
                      </Container>
                    </Container>
                  )}
                {/** Store credit card checkbox */}
                {allowStoringCard &&
                  isNewCreditCardFormShown &&
                  !selectedStoredCard && (
                    <Container px="8" pb="6">
                      <Checkbox
                        checked={toggleStorePaymentMethod}
                        name={'toggleStorePaymentMethod'}
                        label={translations.tk_saveCreditCard}
                        onChangeHandler={() => {
                          setMainStateProperty(
                            'toggleStorePaymentMethod',
                            !toggleStorePaymentMethod
                          )
                        }}
                        data-testing="Checkout_Checkbox_SaveCreditCard"
                      />
                    </Container>
                  )}
              </Container>
            </Container>
          </Container>
        )}
      </Container>
    </React.Fragment>
  )
}

export default CheckoutCreditCard
